import React, { FC, useCallback, useEffect, useState } from 'react';
import { useCart } from '~Components/CartProvider';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import { MEMBERSHIP_DISCOUNT_ID } from '~Components/LinkUser/consts';
import { CreateLoyaltyMemberInput, LoyaltyMember, useAdyen, useLogger, useLoyalty } from '~Hooks';
import useI18n from '~Translations';
import { DISCOUNT_TYPE } from '../../../__queries__';
import { CardIdentity } from '~reducers';
import ChooseStep from './Components/ChooseStep';
import CreateStep from './Components/CreateStep';
import MemberView from './Components/MemberView';
import SearchStep from './Components/SearchStep';
import { displayName, memberSince } from './helpers';
import { ContactlessIcon } from './icons';
import {
  Eyebrow,
  Header,
  Heading,
  MemberHeader,
  MemberMeta,
  MemberName,
  Body,
  Waiting,
  WaitingLabel,
  Panel,
} from './styles';
import Modal from '~ComponentsImproved/Modal';
import NotOnboarded from './Components/NotOnboarded';

// The POS bottom bar renders this icon on the Loyalty button while a card wait
// is running with the modal closed.
export { ContactlessIcon } from './icons';

type Step = 'choose' | 'identifying' | 'search' | 'create' | 'found';

interface Props {
  isShowing: boolean;
  hide: () => void;
  terminalId?: string;
  /**
   * A card acquired while this modal was closed. The wait happens outside the
   * modal so staff can keep ringing up the order, and the result arrives here.
   * Whether a member is behind it is resolved in here, not by the owner.
   */
  pendingCard?: CardIdentity | null;
  /** Why the acquisition failed, shown back on the method picker. */
  pendingError?: string | null;
  /** Asks the owner to close this and start waiting for a card. */
  onRequestTapCard: () => void;
  /** Clears whatever the owner is holding, once acted on. */
  onClearPending: () => void;
  /** True when linking a loyalty member to a table cheque (TSR) instead of the POS cart. */
  isTsr?: boolean;
  /** The cheque to attach the member to, when [[isTsr]] is true. */
  selectedCheque?: string | null;
  /** The member currently attached to [[selectedCheque]], if any. */
  selectedChequeUserId?: string | null;
}

/**
 * Loyalty member identification. The shopper presents their card, we identify
 * them from the PAR, and confirming attaches both the member discount and the
 * card itself to the cart - so the same card pays without being presented
 * twice. Members can also be found by search, or enrolled on the spot.
 *
 * All backend access lives in [[useLoyalty]]; this component owns the flow and
 * the cart, the step components under it own the pixels.
 */
const LoyaltyModal: FC<Props> = ({
  isShowing,
  hide,
  terminalId,
  pendingCard,
  pendingError,
  onRequestTapCard,
  onClearPending,
  isTsr,
  selectedCheque,
  selectedChequeUserId,
}) => {
  const { i18n } = useI18n();
  const cart = useCart();
  const adyen = useAdyen();
  const tableApi = useTableApi();
  const tableUniverse = useTableUniverse();
  const loyalty = useLoyalty();
  const logger = useLogger('loyalty');

  const [step, setStep] = useState<Step>('choose');
  const [error, setError] = useState<string | null>(null);
  const [cardIdentity, setCardIdentity] = useState<CardIdentity | null>(null);
  const [member, setMember] = useState<LoyaltyMember | null>(null);
  /** Why an already-attached member's details could not be refetched. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isReleasing, setIsReleasing] = useState(false);
  /** True when the member on screen is already attached to the cart. */
  const [isConnected, setIsConnected] = useState(false);
  /** The card was read fine, but no member is registered to it. */
  const [notFound, setNotFound] = useState(false);
  /** The member was resolved from their own card, so there is nothing to link. */
  const [cardBelongsToMember, setCardBelongsToMember] = useState(false);
  /** Staff ticked "remember this card" for a member identified without one. */
  const [connectCard, setConnectCard] = useState(false);

  const reset = () => {
    setStep('choose');
    setError(null);
    setCardIdentity(null);
    setMember(null);
    setLoadError(null);
    setIsConnected(false);
    setNotFound(false);
    setConnectCard(false);
    setCardBelongsToMember(false);
  };

  /**
   * Resolves who owns the tapped card. Found, they go straight to the member
   * view; not found, the picker returns with the card still in hand - it is
   * still good for the payment, and finding the shopper by another route (or
   * enrolling them) will connect this card to that member.
   */
  const identifyCard = async (card: CardIdentity) => {
    setStep('identifying');

    try {
      const found = await loyalty.identifyByCard(card);

      if (!found) {
        setNotFound(true);
        setConnectCard(true);
        setStep('choose');
        return;
      }

      setMember(found);
      setCardBelongsToMember(true);
      setStep('found');
    } catch (err) {
      logger.error('Card identification failed', {
        reason: err instanceof Error ? err.message : String(err),
      });
      setError(i18n('Loyalty.IdentifyFailed'));
      setStep('choose');
    }
  };

  /**
   * A member already on the cart reopens straight into their details, but the
   * summary the cart holds is only a name and a level - the stamps and cards
   * are refetched so the view is current.
   */
  const loadAttachedMember = async (id: string) => {
    try {
      setMember(await loyalty.getMember(id));
    } catch (err) {
      logger.error('Could not load the attached loyalty member', {
        memberId: id,
        reason: err instanceof Error ? err.message : String(err),
      });
      setLoadError(i18n('Loyalty.LoadFailed'));
    }
  };

  /**
   * Reopening with a member already on the cart goes straight to their details.
   * Asking someone to identify a shopper who is already identified would be
   * nonsense, and re-tapping would strand a second acquisition on the terminal.
   */
  useEffect(() => {
    if (!isShowing) {
      return;
    }

    const attached = isTsr ? selectedChequeUserId : cart.loyaltyUserId;

    if (attached) {
      setCardIdentity(cart.cardIdentity);
      setIsConnected(true);
      setStep('found');
      loadAttachedMember(attached);
      return;
    }

    setIsConnected(false);
    // The cart can hold a card with no member behind it - a "keep card" from an
    // earlier visit. Carried into this one so finding a member by search links
    // that card, instead of stranding a second acquisition.
    setCardIdentity(pendingCard ?? cart.cardIdentity ?? null);
    setCardBelongsToMember(false);

    // Reopened because a card came back while we were away: find out whose.
    if (pendingCard) {
      setNotFound(false);
      identifyCard(pendingCard);
      return;
    }

    setNotFound(false);
    setError(pendingError ?? null);
    setStep('choose');
  }, [isShowing, pendingCard, pendingError, isTsr, selectedChequeUserId]);

  /** A member picked from the search results. */
  const selectMember = (found: LoyaltyMember) => {
    logger.info('Loyalty member selected from search', {
      memberId: found.id,
      // Whether this search rescued a tapped card nobody was registered to.
      hadUnclaimedCard: Boolean(cardIdentity) && notFound,
    });
    setError(null);
    setNotFound(false);
    setMember(found);
    setStep('found');
  };

  /** Enrolment, handed to the create step. Throws back into its error note. */
  const createMember = async (input: CreateLoyaltyMemberInput) => {
    const created = await loyalty.createMember(input);

    setMember(created);
    setConnectCard(true);
    setStep('found');
  };

  const tapCard = () => {
    if (!terminalId) {
      setError(i18n('Loyalty.NoTerminal'));
      return;
    }

    setError(null);
    onRequestTapCard();
  };

  /** Hands the terminal back, for a card the cart has not taken ownership of. */
  const releaseCard = async (card: CardIdentity | null = cardIdentity) => {
    if (!card || !terminalId) {
      return;
    }

    setIsReleasing(true);

    try {
      const res = await adyen.cancelCardAcquisition(terminalId);

      if (!res.success) {
        logger.warn(`Could not release the card acquisition: ${res.message}`, { terminalId });
      }
    } finally {
      setIsReleasing(false);
    }
  };

  const confirm = () => {
    if (!member) {
      return;
    }

    const level = member.currentMembershipLevel;
    const levelName = level ? i18n(level.name) : '';
    const name = displayName(member, i18n);

    // TSR: attach the member to the cheque via a table event instead of the cart.
    if (isTsr && selectedCheque) {
      tableApi.setChequeUser(selectedCheque, member.id, connectCard);

      if (level && level.discountPercentage > 0) {
        const chequeItems = tableUniverse.items.filter((item) => item.chequeId === selectedCheque);

        tableApi.applyDiscount(selectedCheque, {
          offerId: MEMBERSHIP_DISCOUNT_ID,
          type: DISCOUNT_TYPE.PERCENTAGE,
          rate: level.discountPercentage,
          applyPersistentDiscount: true,
          selectedItemIds: chequeItems
            .filter((item) => !item.disableMembershipDiscount)
            .map((item) => item.id),
        });
      }

      logger.info('Loyalty member confirmed on cheque', {
        memberId: member.id,
        chequeId: selectedCheque,
        levelId: level?.id,
        discountPercentage: level?.discountPercentage ?? 0,
      });

      onClearPending();
      reset();
      hide();
      return;
    }

    // Only set when the member was identified by card. Identified any other way
    // there is nothing acquired on the terminal, so the payment runs as normal.
    if (cardIdentity) {
      cart.setCardIdentity({ ...cardIdentity, memberName: name });
    }

    cart.setLoyaltyUserId(member.id);
    cart.setLoyaltyUserName(name);
    // Either link the card in hand, or keep whichever card ends up paying.
    cart.setConnectCardToMember(connectCard);

    if (level && level.discountPercentage > 0) {
      cart.setAppliedDiscount({
        id: MEMBERSHIP_DISCOUNT_ID,
        type: DISCOUNT_TYPE.PERCENTAGE,
        value: level.discountPercentage,
        title: `${i18n('Loyalty.Title')} · ${levelName}`,
      });
    } else if (cart.appliedDiscount?.id === MEMBERSHIP_DISCOUNT_ID) {
      // Switching to a member with no discount: the old member's discount is
      // ours to take back. Any other discount stays put.
      cart.setAppliedDiscount(null);
    }

    logger.info('Loyalty member confirmed', {
      memberId: member.id,
      levelId: level?.id,
      discountPercentage: level?.discountPercentage ?? 0,
      hasPar: Boolean(cardIdentity?.par),
      identifiedByCard: cardBelongsToMember,
      connectCard,
    });

    onClearPending();
    reset();
    hide();
  };

  /** Backing out before the member is attached to anything. */
  const cancel = useCallback(async () => {
    // The cart never took the card, so the terminal is still holding it and
    // will report Busy to the next transaction. Hand it back.
    await releaseCard();

    onClearPending();
    reset();
    hide();
  }, [cardIdentity, terminalId]);

  /**
   * Closing the view of an already-attached member. Deliberately does not
   * release the card: the cart owns it now and the payment needs it.
   */
  const close = () => {
    reset();
    hide();
  };

  /** Detaches the member, their discount, and their card from the order. */
  const disconnect = async () => {
    // TSR: detach the member from the cheque via a table event instead of the cart.
    if (isTsr && selectedCheque) {
      tableApi.applyDiscount(selectedCheque, {
        rate: 0,
        type: DISCOUNT_TYPE.PRICE,
        selectedItemIds: [],
        applyPersistentDiscount: true,
      });
      tableApi.setChequeUser(selectedCheque, null, false);

      logger.info('Loyalty member disconnected from cheque', {
        memberId: selectedChequeUserId,
        chequeId: selectedCheque,
      });

      onClearPending();
      reset();
      hide();
      return;
    }

    // Only the card path leaves an acquisition on the terminal. A member found
    // by search has none, so there is nothing to hand back and staff should
    // not be shown "cancelling in the terminal".
    if (cart.cardIdentity) {
      await releaseCard(cart.cardIdentity);
    }

    const disconnectedId = cart.loyaltyUserId;

    cart.setLoyaltyUserId(null);
    cart.setLoyaltyUserName(null);
    cart.clearLoyalty();

    // Only ours to clear - a manual or offer discount stays put.
    if (cart.appliedDiscount?.id === MEMBERSHIP_DISCOUNT_ID) {
      cart.setAppliedDiscount(null);
    }

    logger.info('Loyalty member disconnected', { memberId: disconnectedId });

    onClearPending();
    reset();
    hide();
  };

  /**
   * Keeps the acquired card on the cart with no member behind it. The order
   * still pays on the tap that already happened.
   */
  const keepCard = () => {
    if (cardIdentity) {
      cart.setCardIdentity(cardIdentity);
    }

    onClearPending();
    reset();
    hide();
  };

  // Escape closes, except while the lookup is mid-flight.
  useEffect(() => {
    if (!isShowing) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && step !== 'identifying') {
        cancel();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isShowing, step, cancel]);

  if (!isShowing) {
    return null;
  }

  if (!loyalty.isLoyaltyFullySetup) {
    return (
      <Modal isShowing={isShowing} hide={hide}>
        <NotOnboarded onClose={hide} />
      </Modal>
    );
  }

  return (
    <Modal isShowing={isShowing} hide={hide}>
      <Panel>
        <Header>
          <Eyebrow>{i18n('Loyalty.Title')}</Eyebrow>

          {step === 'choose' && <Heading>{i18n('Loyalty.ChooseMethod')}</Heading>}
          {step === 'search' && <Heading>{i18n('Loyalty.Search')}</Heading>}
          {step === 'create' && <Heading>{i18n('Loyalty.CreateUser')}</Heading>}
          {step === 'identifying' && <Heading>{i18n('Loyalty.Identifying')}</Heading>}

          {step === 'found' && member && (
            <MemberHeader>
              <div>
                <MemberName>{displayName(member, i18n)}</MemberName>
                <MemberMeta>
                  {member.currentMembershipLevel
                    ? `${i18n(member.currentMembershipLevel.name)} · `
                    : ''}
                  {i18n('Loyalty.MemberSince')} {memberSince(member.registeredAt)}
                </MemberMeta>
              </div>
            </MemberHeader>
          )}
        </Header>

        {step === 'choose' && (
          <ChooseStep
            notFound={notFound}
            isReleasing={isReleasing}
            error={error}
            hasHeldCard={Boolean(cardIdentity)}
            onSearch={() => {
              setError(null);
              setStep('search');
            }}
            onTapCard={tapCard}
            onCreateUser={() => {
              setError(null);
              setStep('create');
            }}
            onKeepCard={keepCard}
          />
        )}

        {step === 'search' && (
          <SearchStep
            heldCard={notFound && Boolean(cardIdentity)}
            searchState={loyalty.searchState}
            onSearch={loyalty.search}
            onSelect={selectMember}
            onBack={() => setStep('choose')}
            onKeepCard={keepCard}
          />
        )}

        {step === 'create' && (
          <CreateStep onCreate={createMember} onBack={() => setStep('choose')} />
        )}

        {step === 'identifying' && (
          <Body>
            <Waiting>
              <ContactlessIcon size={64} animated />
              <WaitingLabel>{i18n('Loyalty.Identifying')}</WaitingLabel>
            </Waiting>
          </Body>
        )}

        {step === 'found' && (
          <MemberView
            member={member}
            loadError={loadError}
            cardIdentity={cardIdentity}
            isConnected={isConnected}
            isReleasing={isReleasing}
            cardBelongsToMember={cardBelongsToMember}
            connectCard={connectCard}
            onToggleConnectCard={() => setConnectCard((on) => !on)}
            onConfirm={confirm}
            onCancel={cancel}
            onClose={close}
            onDisconnect={disconnect}
          />
        )}
      </Panel>
    </Modal>
  );
};

export default LoyaltyModal;
